import React from 'react';
import { Avatar, Box, Card, CardActionArea, Typography } from '@mui/material';
import { MdDirectionsCar, MdPerson } from 'react-icons/md';

interface PartsVehicleSelectProps {
  vehicles: any[];
  /** Selected vehicle id, or null for "Driver selects at start". */
  selectedVehicleId: number | null;
  onSelect: (vehicleId: number | null) => void;
}

/**
 * Vehicle picker for the parts run builder — a card per vehicle plus a
 * "Driver selects at start" card. Mirrors the Mobile Service Build Service Run
 * vehicle cards. Selecting a vehicle sets vehicle_id; the driver-picks card
 * clears it (null), which the driver app's select-vehicle screen handles.
 */
const PartsVehicleSelect: React.FC<PartsVehicleSelectProps> = ({ vehicles, selectedVehicleId, onSelect }) => {
  const driverPicks = selectedVehicleId == null;

  const CardOption = ({
    selected,
    onClick,
    icon,
    title,
    subtitle,
  }: {
    selected: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    title: string;
    subtitle?: string;
  }) => (
    <Card
      variant='outlined'
      sx={{ border: 2, borderColor: selected ? 'primary.main' : 'divider', transition: 'border-color 0.15s' }}
    >
      <CardActionArea onClick={onClick} sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'flex-start', gap: 1.5 }}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: 'grey.200', color: 'text.secondary' }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant='body2' sx={{ fontWeight: 600 }} noWrap>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant='caption' color='text.secondary'>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
      </CardActionArea>
    </Card>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {vehicles.map((v: any) => (
        <CardOption
          key={v.id}
          selected={selectedVehicleId === v.id}
          onClick={() => onSelect(v.id)}
          icon={<MdDirectionsCar size={20} />}
          title={`${v.year ?? ''} ${v.make ?? ''} ${v.model ?? ''}`.trim() || `Vehicle ${v.id}`}
          subtitle={v.license_plate || undefined}
        />
      ))}
      <CardOption
        selected={driverPicks}
        onClick={() => onSelect(null)}
        icon={<MdPerson size={20} />}
        title='Driver selects at start'
        subtitle='Driver picks their vehicle when they claim the run'
      />
    </Box>
  );
};

export default PartsVehicleSelect;
